import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import ScrollToTop from "../../components/scroll-to-top";
import axiosInstance from "../../utils/axiosInstance";
import useCartStore from "../../store/cartStore";
import { handleGetLanguage } from "../../utils/languageStorage";
import useUserData from "../../store/userStore";

export default function ShopCheckOut() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const { user } = useUserData((state) => state);
    const { cart } = useCartStore((state) => state);
    const [data, setData] = useState(null);

    const productQuery = queryParams.get("product");
    const colorQuery = queryParams.get("color");
    const sizeQuery = queryParams.get("size");
    const qtnQuery = queryParams.get("qtn");
    const referralFromQuery = queryParams.get("referral");

    useEffect(() => {
        if (productQuery) {
            axiosInstance.get(`/products/${productQuery}`).then((res) => {
                setData(res.data.product);
            });
        }
    }, [productQuery]);

    const language = handleGetLanguage();
    const [city, setCity] = useState("");
    const [firstName, setFirstName] = useState(user.name || "");
    const [lastName, setLastName] = useState("");
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState(user?.phone_number || "");
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    useEffect(() => {
        if (city && firstName && lastName && address && phone) {
            setIsButtonDisabled(false);
        } else {
            setIsButtonDisabled(true);
        }
    }, [city, firstName, lastName, address, phone]);

    const totalPrice = () => {
        return cart.reduce((total, item) => {
            const price = item.product.discounted_price || item.product.price;
            return total + price * item.quantity;
        }, 0);
    };

    const translations = {
        en: {
            fashion: "Checkout",
            cartzio: "Cartzio",
            checkout: "Checkout",
            billingAddress: "Billing address",
            city: "City/Region",
            firstName: "First Name",
            lastName: "Last Name",
            username: "Username",
            yourEmail: "Your Email",
            address: "Address",
            phone: "Phone",
            continueCheckout: "Continue to checkout",
            yourCart: "Your Cart",
            color: "Color",
            size: "Size",
            count: "Quantity",
            total: "Total (GEL)",
        },
        ka: {
            fashion: "გადახდა",
            cartzio: "კარტციო",
            checkout: "გადახდა",
            billingAddress: "ბილინგის მისამართი",
            city: "ქალაქი/რაეგიონი",
            firstName: "სახელი",
            lastName: "გვარი",
            username: "მომხმარებლის სახელი",
            yourEmail: "თქვენი ელ.ფოსტა",
            address: "მისამართი",
            phone: "ტელეფონი",
            continueCheckout: "გაგრძელება გადახდაზე",
            yourCart: "თქვენი კალათა",
            color: "ფერი",
            size: "ზომა",
            count: "რაოდენობა",
            total: "სულ (GEL)",
        },
    };

    const t = translations[language] || translations.en;

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const values = Object.fromEntries(formData.entries());

        const checkoutType = !productQuery && !colorQuery && !qtnQuery && !data ? "cart" : "direct";

        let payload;

        if (checkoutType === "cart") {
            // Pass full cart data
            payload = {
                ...values,
                cart_items: cart.map((item) => ({
                    product_id: item.product_id,
                    product_option_id: item.product_option_id,
                    size_id: item.size_id || null, // 🔥 Pass size_id here
                    quantity: item.quantity,
                    price: item.price,
                })),
            };
        } else {
            // Direct product checkout
            payload = {
                ...values,
                product_id: productQuery,
                product_option_id: data.options?.[0]?.id || null,
                quantity: +qtnQuery,
                size_id: +sizeQuery || null, // 🔥 Pass size_id here
                ...(referralFromQuery && { referral: referralFromQuery }),
            };
        }

        try {
            setIsButtonDisabled(true);
            const res = await axiosInstance.post(`/payments/checkout/${checkoutType}`, payload);
            if (res?.status === 200) {
                window.location.href = res?.data?.data?._links?.redirect?.href;
            }
        } catch (err) {
            console.log("Checkout error response:", err);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    return (
        <>
            <Navbar navClass="defaultscroll is-sticky" />
            <section className="relative table w-full py-20 lg:py-24 md:pt-28 bg-gray-50 dark:bg-slate-800">
                <div className="container relative">
                    <div className="grid grid-cols-1 mt-14">
                        <h3 className="text-3xl leading-normal font-semibold">{t.fashion}</h3>
                    </div>

                    <div className="relative mt-3">
                        <ul className="tracking-[0.5px] mb-0 inline-block">
                            <li className="inline-block uppercase text-[13px] font-bold duration-500 ease-in-out hover:text-orange-500">
                                <Link to="/">{t.cartzio}</Link>
                            </li>
                            <li className="inline-block text-base text-slate-950 dark:text-white mx-0.5 ltr:rotate-0 rtl:rotate-180">
                                <i className="mdi mdi-chevron-right"></i>
                            </li>
                            <li
                                className="inline-block uppercase text-[13px] font-bold text-orange-500"
                                aria-current="page"
                            >
                                {t.checkout}
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="relative md:py-24 py-16">
                <div className="container relative">
                    <form onSubmit={handleFormSubmit}>
                        <div className="grid lg:grid-cols-12 md:grid-cols-2 grid-cols-1 gap-6">
                            <div className="lg:col-span-8">
                                <div className="p-6 rounded-md shadow dark:shadow-gray-800">
                                    <h3 className="text-xl leading-normal font-semibold">
                                        {t.billingAddress}
                                    </h3>

                                    <div className="grid lg:grid-cols-12 grid-cols-1 mt-6 gap-5">
                                        <div className="lg:col-span-12">
                                            <label className="form-label font-semibold">
                                                {t.city}: <span className="text-red-600">*</span>
                                            </label>
                                            <input
                                                className="w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0 mt-2"
                                                name="city"
                                                id="city"
                                                required
                                                value={city}
                                                onChange={(e) => setCity(e.target.value)}
                                            />
                                        </div>
                                        <div className="lg:col-span-6">
                                            <label className="form-label font-semibold">
                                                {t.firstName}:{" "}
                                                <span className="text-red-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0 mt-2"
                                                placeholder={t.firstName}
                                                id="name"
                                                name="name"
                                                required
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                            />
                                        </div>

                                        <div className="lg:col-span-6">
                                            <label className="form-label font-semibold">
                                                {t.lastName}:{" "}
                                                <span className="text-red-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0 mt-2"
                                                placeholder={t.lastName}
                                                id="surname"
                                                name="surname"
                                                required
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                            />
                                        </div>

                                        <div className="lg:col-span-12">
                                            <label className="form-label font-semibold">
                                                {t.address}: <span className="text-red-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0 mt-2"
                                                placeholder={t.address}
                                                id="address"
                                                name="address"
                                                required
                                                value={address}
                                                onChange={(e) => setAddress(e.target.value)}
                                            />
                                        </div>
                                        <div className="lg:col-span-12">
                                            <label className="form-label font-semibold">
                                                {t.phone}
                                                <span className="text-red-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0 mt-2"
                                                placeholder={t.phone}
                                                id="phone_number"
                                                name="phone_number"
                                                required
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <button
                                            type="submit"
                                            className="py-2 px-5 inline-block tracking-wide align-middle duration-500 text-base text-center bg-orange-500 text-white rounded-md w-full disabled:opacity-70"
                                            disabled={isButtonDisabled}
                                        >
                                            {t.continueCheckout}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="lg:col-span-4">
                                <div className="p-6 rounded-md shadow dark:shadow-gray-800">
                                    {!productQuery && !colorQuery && !qtnQuery && (
                                        <div className="flex justify-between items-center">
                                            <h5 className="text-lg font-semibold">{t.yourCart}</h5>

                                            <Link
                                                to="#"
                                                className="bg-orange-500 flex justify-center items-center text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full h-5"
                                            >
                                                {cart.length !== 0 ? cart.length : 1}
                                            </Link>
                                        </div>
                                    )}

                                    {!productQuery || !qtnQuery || !data ? (
                                        <div className="mt-4 rounded-md shadow dark:shadow-gray-800">
                                            {cart.map((item) => {
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className="p-3 flex justify-between items-center"
                                                    >
                                                        <div>
                                                            <h5 className="font-semibold">
                                                                {item?.product?.name[language]}
                                                            </h5>
                                                            <div className="text-xs text-slate-400 flex gap-4">
                                                                <p>
                                                                    {t.color} :{" "}
                                                                    {
                                                                        item.product.colors[0].name[
                                                                            language
                                                                        ]
                                                                    }
                                                                </p>
                                                                <p>
                                                                    {t.size} : {item.size}
                                                                </p>
                                                                <p>
                                                                    {t.count} : {item.quantity}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <p className="text-slate-400 font-semibold">
                                                            GEL{" "}
                                                            {item.quantity *
                                                                item?.product?.discounted_price ||
                                                                item.product.price}
                                                        </p>
                                                    </div>
                                                );
                                            })}

                                            <div className="p-3 flex justify-between items-center border border-gray-100 dark:border-gray-800">
                                                <div>
                                                    <h5 className="font-semibold">{t.total}</h5>
                                                </div>

                                                <p className="font-semibold">{totalPrice()}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mt-4 px-2 rounded-md shadow dark:shadow-gray-800 ">
                                            <div className="p-3 flex justify-between items-center">
                                                <div>
                                                    <h5 className="font-semibold">
                                                        {data?.name?.[language]}
                                                    </h5>
                                                    <div className="text-xs text-slate-400 flex gap-4">
                                                        <p>
                                                            {t.color} :{" "}
                                                            {data.colors[0].name[language]}
                                                        </p>
                                                        {/* <p>
                                                            {t.size} : {size?.name}
                                                        </p> */}
                                                        <p>
                                                            {t.count} : {qtnQuery}
                                                        </p>
                                                    </div>
                                                </div>

                                                <p className="text-slate-400 font-semibold">
                                                    GEL{" "}
                                                    {qtnQuery * data?.discounted_price
                                                        ? data?.discounted_price
                                                        : data?.price}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </section>
            <Footer />
            <ScrollToTop />
        </>
    );
}
